/*
 * Project: bellcloud
 * File: packages/utils/device/converters/wireTypeCodec.ts
 * Author: SWAN
 * Created: 2025-11-03
 * Description: 와이어 타입(u8/u16/배열…) ↔ 바이트 (TypeConverter)
 */

/**
 * TypeConverter: 패킷 바이트를 원시 값으로 변환 (또는 그 반대)
 *
 * 역할:
 * - Packet → JSON: bytes → raw value (type 기반)
 * - JSON → Packet: raw value → bytes (type 기반)
 *
 * 지원하는 타입:
 * - Primitives: u8, u16, u32, s8, s16, s32
 * - Arrays: au8[], au16[], as16[], as32[]
 */
export class TypeConverter {
  /**
   * 바이트 배열을 원시 값으로 변환 (Packet → JSON)
   * @param bytes 원시 바이트 배열
   * @param type 데이터 타입 (u8, u16, s32, au8[] 등)
   * @param debug 디버그 정보 (선택)
   * @returns 변환된 원시 값
   */
  static bytesToValue(bytes: number[], type: string): unknown {
    // Unsigned integers
    if (type === 'u8') {
      const result = bytes[0] ?? 0;
      return result;
    }

    if (type === 'u16') {
      const result = this.fromLittleEndian(bytes);

      return result;
    }

    if (type === 'u32') {
      const result = this.fromLittleEndian(bytes);
      return result;
    }

    // Signed integers
    if (type === 's8') {
      const val = bytes[0] ?? 0;
      const result = val >= 0x80 ? val - 0x100 : val;
      return result;
    }

    if (type === 's16') {
      const val = this.fromLittleEndian(bytes);
      const result = val >= 0x8000 ? val - 0x10000 : val;
      return result;
    }

    if (type === 's32') {
      const val = this.fromLittleEndian(bytes);
      const result = val >= 0x80000000 ? val - 0x100000000 : val;
      return result;
    }

    // Arrays - unsigned
    if (type === 'au8[]') {
      const result = Array.from(bytes);
      return result;
    }

    if (type === 'au16[]') {
      const count = Math.floor(bytes.length / 2);
      const result = Array.from({ length: count }, (_, i) =>
        this.fromLittleEndian(bytes.slice(i * 2, i * 2 + 2))
      );

      return result;
    }

    // Arrays - signed
    if (type === 'as16[]') {
      const count = Math.floor(bytes.length / 2);
      const result = Array.from({ length: count }, (_, i) => {
        const val = this.fromLittleEndian(bytes.slice(i * 2, i * 2 + 2));
        return val >= 0x8000 ? val - 0x10000 : val;
      });

      return result;
    }

    if (type === 'as32[]') {
      const count = Math.floor(bytes.length / 4);
      const result = Array.from({ length: count }, (_, i) => {
        const val = this.fromLittleEndian(bytes.slice(i * 4, i * 4 + 4));
        return val >= 0x80000000 ? val - 0x100000000 : val;
      });

      return result;
    }

    // Default: return raw bytes
    const result = bytes;

    return result;
  }

  /**
   * 원시 값을 바이트 배열로 변환 (JSON → Packet)
   * @param value 원시 값
   * @param type 데이터 타입
   * @param byteSize 바이트 크기
   * @returns 변환된 바이트 배열
   */
  static valueToBytes(value: unknown, type: string, byteSize: number): number[] {
    // Unsigned integers
    if (type === 'u8' || type === 'u16' || type === 'u32') {
      const result = this.toLittleEndian(value as number, byteSize);
      return result;
    }

    // Signed integers
    if (type === 's8' || type === 's16' || type === 's32') {
      const num = value as number;
      // Convert negative to unsigned representation
      const unsigned = num < 0 ? (1 << (byteSize * 8)) + num : num;
      const result = this.toLittleEndian(unsigned, byteSize);
      return result;
    }

    // Arrays - unsigned
    if (type === 'au8[]') {
      if (Array.isArray(value)) {
        const paddedArray = new Array(byteSize).fill(0);
        (value as number[]).forEach((val, index) => {
          if (index < byteSize) paddedArray[index] = val;
        });

        return paddedArray;
      }
      const result = new Array(byteSize).fill(0);

      return result;
    }

    if (type === 'au16[]') {
      if (Array.isArray(value)) {
        const result: number[] = [];
        (value as number[]).forEach(val => {
          result.push(...this.toLittleEndian(val, 2));
        });
        // Pad to byteSize
        while (result.length < byteSize) {
          result.push(0);
        }
        return result.slice(0, byteSize);
      }
      return new Array(byteSize).fill(0);
    }

    // Arrays - signed
    if (type === 'as16[]') {
      if (Array.isArray(value)) {
        const result: number[] = [];
        (value as number[]).forEach(val => {
          const unsigned = val < 0 ? 0x10000 + val : val;
          result.push(...this.toLittleEndian(unsigned, 2));
        });
        while (result.length < byteSize) {
          result.push(0);
        }
        return result.slice(0, byteSize);
      }
      return new Array(byteSize).fill(0);
    }

    if (type === 'as32[]') {
      if (Array.isArray(value)) {
        const result: number[] = [];
        (value as number[]).forEach(val => {
          const unsigned = val < 0 ? 0x100000000 + val : val;
          result.push(...this.toLittleEndian(unsigned, 4));
        });
        while (result.length < byteSize) {
          result.push(0);
        }
        return result.slice(0, byteSize);
      }
      return new Array(byteSize).fill(0);
    }

    // Default: fill with zeros
    return new Array(byteSize).fill(0);
  }

  /**
   * Little Endian 바이트 배열을 숫자로 변환
   * @param bytes 바이트 배열
   * @returns 변환된 숫자
   */
  private static fromLittleEndian(bytes: number[]): number {
    return bytes.reduce((acc, byte, index) => acc + (byte << (8 * index)), 0);
  }

  /**
   * 숫자를 Little Endian 바이트 배열로 변환
   * @param value 숫자
   * @param byteSize 바이트 크기
   * @returns Little Endian 바이트 배열
   */
  private static toLittleEndian(value: number, byteSize: number): number[] {
    return Array.from({ length: byteSize }, (_, i) => (value >> (8 * i)) & 0xff);
  }
}
